import { Tooltip as AlchemyTooltip } from '@m/alchemy-ui/Tooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import { ConfirmCheckmarkIcon } from '@m/alchemy-ui/icons/ConfirmCheckmarkIcon';
import { EditIcon } from '@m/alchemy-ui/icons/EditIcon';
import { ThumbsUpIcon } from '@m/alchemy-ui/icons/ThumbsUpIcon';
import { RocketIcon } from '../../../common/icons';
import { useRuntimeCommunity } from '../../../context';
import { type MilestoneData } from '../IdeaCard.types';
import * as Styled from './IdeaCard.styled';

const getMilestoneIcon = (conditionType?: string | null): React.ReactNode => {
  switch (conditionType) {
    case 'review':
      return <ConfirmCheckmarkIcon aria-hidden="true" />;
    case 'score':
      return <ThumbsUpIcon aria-hidden="true" />;
    case 'elaborate':
      return <EditIcon aria-hidden="true" />;
    default:
      return <RocketIcon />;
  }
};

interface MilestoneBadgeProps {
  readonly lastReachedMilestone: MilestoneData;
  readonly currentMilestone: MilestoneData | null;
}

export const MilestoneBadge = ({ lastReachedMilestone, currentMilestone }: MilestoneBadgeProps) => {
  const { colors } = useRuntimeCommunity();
  const intl = useIntl();
  const bgColor = colors.voteBar ? `#${colors.voteBar}` : '#99A1DB';

  return (
    <Styled.MilestoneBadge $bgColor={bgColor} role="region">
      {getMilestoneIcon(lastReachedMilestone.condition?.type)}
      <span>{lastReachedMilestone.name}</span>
      {currentMilestone && (
        <AlchemyTooltip
          trigger={
            <Styled.MilestoneBadgeInfo
              aria-label={intl.formatMessage({ id: 'milestoneInfo', defaultMessage: 'Milestone info' })}
            >
              {String('i')}
            </Styled.MilestoneBadgeInfo>
          }
          placement="top"
          contents={currentMilestone.name ?? ''}
        />
      )}
    </Styled.MilestoneBadge>
  );
};

interface MilestoneExpiredBadgeProps {
  readonly isExpired: boolean;
}

export const MilestoneExpiredBadge = ({ isExpired }: MilestoneExpiredBadgeProps) => {
  const intl = useIntl();
  const { colors } = useRuntimeCommunity();
  const bgColor = colors.voteBar ? `#${colors.voteBar}` : '#99A1DB';

  if (isExpired) {
    return (
      <AlchemyTooltip
        trigger={
          <Styled.MilestoneBadge $bgColor={bgColor} role="region">
            <FormattedMessage id="expired" defaultMessage="Expired" />
          </Styled.MilestoneBadge>
        }
        placement="top"
        contents={intl.formatMessage({
          id: 'expiredTooltip',
          defaultMessage: 'This idea did not complete all the milestones within the time limit'
        })}
      />
    );
  }

  return (
    <AlchemyTooltip
      trigger={
        <Styled.MilestoneBadge $bgColor={bgColor} role="region">
          <FormattedMessage id="notApproved" defaultMessage="Not Approved" />
        </Styled.MilestoneBadge>
      }
      placement="top"
      contents={intl.formatMessage({
        id: 'notApprovedTooltip',
        defaultMessage: 'This idea has not been approved by a moderator'
      })}
    />
  );
};
